import React from 'react';
import { motion } from 'framer-motion';
import { FiAward, FiUsers, FiThumbsUp, FiShield } from 'react-icons/fi';
import SafeIcon from '../components/SafeIcon';
import PageHeader from '../components/shared/PageHeader';
import CtaSection from '../components/shared/CtaSection';

const About = () => {
  const stats = [
    { icon: FiAward, value: "15+", label: "Years Experience" },
    { icon: FiUsers, value: "10k+", label: "Happy Customers" },
    { icon: FiShield, value: "100%", label: "Licensed & Insured" },
    { icon: FiThumbsUp, value: "4.9/5", label: "Average Rating" }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader 
        title="About NorthPeak" 
        subtitle="Your local Montreal HVAC experts committed to honest pricing, quality workmanship, and exceptional customer service."
        image="https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&q=80&w=2000"
      />

      {/* Our Story */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-lg text-muted">
                <p>
                  Founded over 15 years ago in the heart of Montreal, NorthPeak Heating & Cooling started with a simple mission: to provide reliable, honest, and high-quality HVAC services to our local community.
                </p>
                <p>
                  We know that Quebec's climate is unforgiving. From the freezing temperatures of January to the sweltering humidity of July, your comfort depends on systems that work flawlessly. That's why we've built a team of certified, passionate technicians who treat every home like their own.
                </p>
                <p>
                  Unlike large, impersonal corporations, we are locally owned and operated. When you call NorthPeak, you're speaking to a neighbor who understands the urgency of a broken furnace in the dead of winter.
                </p>
              </div>
            </div>
            <div className="lg:w-1/2 relative">
              <img 
                src="https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&q=80&w=1000" 
                alt="HVAC Team" 
                className="rounded-2xl shadow-xl w-full object-cover h-[400px]"
              />
              <div className="absolute -bottom-8 -left-8 bg-accent text-white p-8 rounded-xl shadow-lg hidden md:block">
                <div className="text-4xl font-bold mb-1">15+</div>
                <div className="text-sm font-semibold uppercase tracking-wider">Years of Service</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-primary text-white">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col items-center"
              >
                <SafeIcon icon={stat.icon} className="text-4xl text-accent mb-4" />
                <div className="text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-blue-200 text-sm uppercase tracking-wider font-semibold">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section-padding bg-bg">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Certified & Trusted</h2>
          <p className="text-lg text-muted mb-10">
            We hold all necessary provincial licenses and certifications. Our technicians undergo continuous training to stay updated with the latest HVAC technologies, ensuring we can service any make or model efficiently.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <div className="bg-white px-8 py-4 rounded-lg shadow-sm border border-gray-100 font-bold text-primary">
               RBQ License Holder
            </div>
            <div className="bg-white px-8 py-4 rounded-lg shadow-sm border border-gray-100 font-bold text-primary">
               Fully Insured ($2M Liability)
            </div>
            <div className="bg-white px-8 py-4 rounded-lg shadow-sm border border-gray-100 font-bold text-primary">
               WSIB/CNESST Compliant
            </div>
          </div>
        </div>
      </section>

      <CtaSection />
    </div>
  );
};

export default About;